// First import on purpose: index.css declares the cascade layer order (`@layer reset, tokens, ...`). A CSS Module
// imported earlier would register its `components` layer first and end up below the reset.
import '../index.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { RouteLoadingFallback } from './components/RouteLoadingFallback';
import { lazyWithLocales } from './lib/lazyWithLocales';
import { hasStoredSession } from './lib/storedSession';
import { initClientSentry } from './lib/sentry';
import './lib/i18n'; // i18n initialisation — must be imported before app renders
import { initMotionBootstrap, startMotionBootstrapListener } from './lib/motionBootstrap';
import { bindVisualViewportCss } from './lib/visualViewport';

initMotionBootstrap();
bindVisualViewportCss();
initClientSentry();
// index.html carries the start page's descriptive title for crawlers that do not run
// JS. In the running app, pages without their own title show the product name.
document.title = 'Smidly';

// ─── Light shell (CX-397) ─────────────────────────────────────────────────────
// The marketing pages render here without TanStack Query, axios, the stores or the session
// restore. Every other route, and `/` for a visitor with a stored session, loads the app
// shell on demand.
// Wait for the icon-button tooltip too: if it arrived after the shell rendered, icon buttons
// would remount and could lose focus or a click.
const AppShell = React.lazy(() =>
  Promise.all([
    import('./AppShell'),
    import('./components/ui/button').then((m) => m.preloadButtonTooltip()),
  ]).then(([shell]) => shell),
);
const HomePage = React.lazy(() => import('./features/marketing/HomePage'));
// Public product showcase (no auth guard, and deliberately not PublicOnlyRoute so
// sellers can review their own sales material while logged in).
const ProductShowcasePage = React.lazy(() => import('./features/showcase/ProductShowcasePage'));
const ContactPage = React.lazy(() =>
  import('./features/marketing/ContactPage').then((m) => ({ default: m.ContactPage })),
);
const TermsPage = lazyWithLocales(() =>
  import('./features/marketing/TermsPage').then((m) => ({ default: m.TermsPage })),
  ['legal'],
);
const PrivacyPage = lazyWithLocales(() =>
  import('./features/marketing/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
  ['legal'],
);

// Public pages with their own API calls (CX-489): plain fetch, no auth, so they stay in the light shell.
const PublicQuotePage = lazyWithLocales(() => import('./features/public-quote/PublicQuotePage'));
const BookingWidgetPage = lazyWithLocales(() => import('./features/public-booking/BookingWidgetPage'));
const CancellationPage = lazyWithLocales(
  () => import('./features/public-booking/components/CancellationPage'),
);
const PublicStayPage = lazyWithLocales(() => import('./features/public-stay/PublicStayPage'));
const StayCancellationPage = lazyWithLocales(() => import('./features/public-stay/StayCancellationPage'));
const PublicLandingPage = lazyWithLocales(() => import('./features/public-landing/PublicLandingPage'));
const PublicArticlePage = lazyWithLocales(() => import('./features/publicHelp/PublicArticlePage'));

/** `/`: marketing for a visitor, the app shell (which redirects) when a session may exist. */
function StartRoute() {
  return hasStoredSession() ? <AppShell /> : <HomePage />;
}

function App() {
  React.useEffect(() => startMotionBootstrapListener(), []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <React.Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<StartRoute />} />
          <Route path="/product" element={<ProductShowcasePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/q/:shareToken" element={<PublicQuotePage />} />
          <Route path="/bk/:embedToken" element={<BookingWidgetPage />} />
          <Route path="/bk/cancel/:token" element={<CancellationPage />} />
          <Route path="/stay/cancel/:cancellationToken" element={<StayCancellationPage />} />
          <Route path="/stay/:publicToken" element={<PublicStayPage />} />
          <Route path="/p/:pageId/:slug" element={<PublicLandingPage />} />
          <Route path="/p/:ref" element={<PublicLandingPage />} />
          <Route path="/help/a/:publicToken" element={<PublicArticlePage />} />
          <Route path="*" element={<AppShell />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}

const REACT_ROOT_KEY = '__connexioReactRoot';

function renderApp() {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element not found');

  const host = rootElement as HTMLElement & { [REACT_ROOT_KEY]?: ReactDOM.Root };
  const root = host[REACT_ROOT_KEY] ?? ReactDOM.createRoot(rootElement);
  host[REACT_ROOT_KEY] = root;
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>,
  );
}

renderApp();
